import { defineComponent as _defineComponent } from 'vue'
import type { ObjectType } from '@/@types/graphql-types';

import ProviderIcon from '@/components/picture/ProviderIcon.vue';
import BasicButton from '@/ui-components/BasicButton.vue';

import { ClickoutUrl, handleClickoutUrl, trackClickout } from '@/helpers/clickout-helper';
import {
	SnowplowClickoutContextGraphql,
	SnowplowContext,
	SnowplowTitleContextGraphql,
} from '@/helpers/tracking/providers/snowplow-contexts';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';

import { computed, ref } from 'vue';
import { useI18n } from '@/helpers/composables/useI18n';
import { useAppStore, useLanguageStore } from '@/helpers/composables/useStores';
import { ModalHelper } from '@/helpers/modal-helper';
import { TitleDetail } from '@/interfaces/title-details-graphql';
import { disableATVAffiliation } from '@/constants/buybox-excluded-packages.constant';
import { observableOptions, useOfferImpressionTracking } from '@/helpers/composables/useOfferImpressionTracking';
import { getClickoutTrackingPlacement } from '@/helpers/composables/useProvideClickoutPlacement';
import { ClickoutType } from '@/interfaces/snowplow/clickout-context';
import { inject } from 'vue';
import { MaybeRefOrGetter, toValue } from '@vueuse/core';
import type { WatchNowOfferFragment } from '@/pages/graphql/fragments/WatchNowOffer.fragment';
import type { Maybe } from '@/helpers/types';


export default /*#__PURE__*/_defineComponent({
  __name: 'WatchNowButton',
  props: {
    titleObjectId: null,
    titleObjectType: null,
    title: { default: undefined },
    watchNowOffer: { default: () => ({}) },
    titleSeasonNumber: { default: undefined },
    lazy: { type: Boolean, default: true },
    size: { default: 'default' },
    rounded: { type: Boolean, default: true },
    color: { default: 'secondary' },
    inQuickActions: { type: Boolean, default: false },
    hideIcon: { type: Boolean, default: false },
    overwriteClickTracking: { type: Boolean, default: false },
    useClassicUrl: { type: Boolean, default: false },
    isSponsoredRecommendation: { type: Boolean, default: false },
    sponsoredRecommendationType: { default: 'REGULAR' },
    additionalContexts: { default: () => [] }
  },
  emits: ['watchNowClicked'],
  setup(__props: any, { emit: emits }) {

const props = __props as { titleObjectId: number, titleObjectType: ObjectType, title: TitleDetail, watchNowOffer: Maybe<WatchNowOfferFragment>, titleSeasonNumber: number, lazy: boolean, size: 'default' | 'large' | 'slim', rounded: boolean, color: 'secondary' | 'primary', inQuickActions: boolean, hideIcon: boolean, overwriteClickTracking: boolean, useClassicUrl: boolean, isSponsoredRecommendation: boolean, sponsoredRecommendationType: string, additionalContexts: SnowplowContext[] };





const { country } = useLanguageStore();
const { xServeTrack } = useAppStore();

const watchNowClickoutType = inject<MaybeRefOrGetter<ClickoutType> | undefined>('watchNowClickoutType', undefined);
const { clickoutType, placement } = getClickoutTrackingPlacement('watch_now');
const domainSessionId = ref<string | null>(null);
TrackingHelper.getDomainSessionId().then(sid => (domainSessionId.value = sid));

const titleContext = computed(
	() =>
		new SnowplowTitleContextGraphql(
			props.titleObjectId,
			props.titleObjectType,
			props.titleSeasonNumber,
			undefined,
			undefined,
			undefined,
			props.title?.id
		)
);

// Offer impression tracking
const { onIntersection } = useOfferImpressionTracking({
	additionalContexts: () => [...(props.additionalContexts || []), titleContext.value],
});

const clickoutUrl = computed(() => {
	const url = new ClickoutUrl(props.watchNowOffer, {
		skipRedirect: props.useClassicUrl,
	})
		.setTitleSourceId(props.title?.id ?? null)
		.addContexts(props.additionalContexts)
		.addContexts(titleContext)
		.set('uct_country', country.value)
		.set('sid', domainSessionId.value)
		.set('uct_tryout', 1)
		.updateContext('ClickoutContextGraphql', 'clickoutType', toValue(watchNowClickoutType) ?? clickoutType)
		.updateContext('ClickoutContextGraphql', 'placement', placement)
		.updateContext('TitleContextGraphql', 'offerEntityId', props.title?.id)
		.setCanary(xServeTrack.value);

	if (props.watchNowOffer?.package?.packageId === 350 && disableATVAffiliation.includes(country.value)) {
		url.set('uct_ct', 'no_affiliation');
	}

	// Amazon streaming bounty redirects
	if ([9, 119].includes(props.watchNowOffer?.package?.packageId ?? 0)) {
		return url.set('r', url.get('r')!.replace('app.primevideo.com', 'www.primevideo.com'));
	}

	return url;
});

const providerIcon = computed(() => props.watchNowOffer?.package?.packageId ?? undefined);

const { tc } = useI18n();
const watchNowLabel = computed(() =>
	props.sponsoredRecommendationType === 'CINEMATIC' && props.isSponsoredRecommendation
		? tc('WEBAPP_BUY_YOUR_TICKETS')
		: tc('WEBAPP_SEENLIST_WATCHNOW')
);

const playerByTechnicalName: Record<string, () => Promise<any>> = {
	plexplayer: () => import('@/ui-components/PlexPlayerModal.vue'),
	justwatchtv: () => import('@/ui-components/JwTVModal.vue'),
};

const observeVisibilityOptions = computed(() => {
	return {
		callback: onVisibilityChanged,
		...observableOptions,
	};
});

function onVisibilityChanged(isVisible: boolean) {
	if (props.watchNowOffer) onIntersection(clickoutUrl.value, isVisible);
}

function handleWatchNow() {
	emits('watchNowClicked');

	if (!props.watchNowOffer) return;

	const contexts = [
		...(props.additionalContexts ?? []),
		titleContext.value,
		SnowplowClickoutContextGraphql.fromProviderOffer(props.watchNowOffer),
	];

	// JUSWATCH_TV
	if (['plexplayer', 'justwatchtv'].includes(props.watchNowOffer.package.technicalName)) {
		ModalHelper.openModal(
			playerByTechnicalName[props.watchNowOffer.package.technicalName],
			{ offer: props.watchNowOffer, title: props.title },
			{ cssClass: 'trailer-modal' },
			'',
			true,
			[]
		);
		trackClickout(clickoutUrl.value);
	} else {
		handleClickoutUrl(clickoutUrl.value, false);
	}

	if (!props.overwriteClickTracking) {
		TrackingHelper.trackEvent(
			'userinteraction',
			{
				action: 'watchnow_clicked',
				property: props.inQuickActions + '',
			},
			contexts
		);
	}
}

return { __sfc: true,props, emits, country, xServeTrack, watchNowClickoutType, clickoutType, placement, domainSessionId, titleContext, onIntersection, clickoutUrl, providerIcon, tc, watchNowLabel, playerByTechnicalName, observeVisibilityOptions, onVisibilityChanged, handleWatchNow, ProviderIcon, BasicButton }
}

})